import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { formatPhone } from '#shared';
import { api } from '../../lib/api';
import type { LeadRow } from '../../lib/leads';
import { SearchInput, cx } from '../ui';

/** Find a lead by name, phone or L-number and pick it. Only leads this person can see are listed. */
export function LeadPicker({ value, onChange, exclude, placeholder = 'Type a name, phone or L-number' }: {
  value: LeadRow | null;
  onChange: (lead: LeadRow | null) => void;
  exclude?: string;
  placeholder?: string;
}) {
  const [q, setQ] = useState('');
  const term = q.trim();
  const found = useQuery({
    queryKey: ['lead-picker', term],
    queryFn: () => api.get<{ items: LeadRow[] }>(`/leads?q=${encodeURIComponent(term)}&pageSize=10`),
    enabled: term.length >= 2,
    staleTime: 30_000,
  });
  const items = (found.data?.items ?? []).filter((l) => l.id !== exclude);

  if (value) {
    return (
      <div className="flex items-center gap-3 rounded-lg bg-brand-50 p-3 ring-1 ring-brand-200 dark:bg-brand-950/40 dark:ring-brand-800">
        <Check className="size-5 text-brand-600" />
        <div className="min-w-0 flex-1">
          <p className="font-medium">L-{value.number} {value.name}</p>
          <p className="truncate text-sm text-slate-500">{[value.phone && formatPhone(value.phone), value.ownerName, value.statusName].filter(Boolean).join(' · ')}</p>
        </div>
        <button type="button" className="text-sm font-medium text-brand-700 hover:underline dark:text-brand-400" onClick={() => onChange(null)}>Change</button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <SearchInput value={q} onChange={setQ} placeholder={placeholder} />
      {term.length >= 2 && (
        <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto rounded-lg ring-1 ring-slate-200 dark:divide-slate-800 dark:ring-slate-700">
          {found.isPending ? <li className="p-3 text-sm text-slate-500">Searching…</li>
            : items.length === 0 ? <li className="p-3 text-sm text-slate-500">No lead found. Check the spelling or try the phone number.</li>
            : items.map((l) => (
              <li key={l.id}>
                <button type="button" onClick={() => onChange(l)} className={cx('w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60')}>
                  <p className="font-medium">L-{l.number} {l.name}</p>
                  <p className="truncate text-sm text-slate-500">{[l.phone && formatPhone(l.phone), l.company, l.ownerName ?? 'Not assigned', l.statusName].filter(Boolean).join(' · ')}</p>
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
